import { apiClient } from './apiClient';
import { encryptPassword } from '../auth/authApi';

// API 응답 래퍼
export interface ApiResponse<T> {
  code: number;
  status: string;
  message: string;
  data: T;
}

// 실제 API 응답 데이터 구조
export interface UserApiData {
  person_id: number;
  name: string;
  birth_date: string;
  identity: 'STUDENT' | 'DOCTOR' | 'OTHER' | 'ADMIN';
  department: string;
  email: string;
  phone_number: string;
  approval_status: 'PENDING' | 'APPROVED' | 'REJECTED';
  deactivate_reason: string | null;
}

// 페이지네이션 정보
export interface PageableInfo {
  pageNumber: number;
  pageSize: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  offset: number;
  paged: boolean;
  unpaged: boolean;
}

// API 응답 데이터 구조
export interface UserListApiResponse {
  content: UserApiData[];
  pageable: PageableInfo;
  totalElements: number;
  totalPages: number;
  last: boolean;
  size: number;
  number: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  numberOfElements: number;
  first: boolean;
  empty: boolean;
}

// 컴포넌트에서 사용할 사용자 인터페이스 (기존 유지)
export interface User {
  id: number;
  name: string;
  email: string;
  department: string;
  affiliation: string;
  phone: string;
  status: 'ACTIVE' | 'FROZEN' | 'PENDING' | 'APPROVED' | 'REJECTED';
  category: string;
  createdAt: string;
  lastLoginAt?: string;
  birthDate?: string;
  postsCount: number;
  commentsCount: number;
  profileImageUrl?: string;
  isActive?: boolean; // 계정 활성 상태
  // API 원본 필드들 추가
  person_id?: number;
  birth_date?: string;
  identity?: 'STUDENT' | 'DOCTOR' | 'ADMIN' | 'OTHER';
  phone_number?: string;
  approval_status?: 'PENDING' | 'APPROVED' | 'REJECTED';
  deactivate_reason?: string | null;
}

export interface UserListParams {
  page?: number;
  size?: number;
  category?: string;
  affiliation?: string;
  status?: string;
  search?: string;
  identity?: 'STUDENT' | 'DOCTOR' | 'ADMIN' | 'OTHER';
  department?: string;
  approvalStatus?: 'PENDING' | 'APPROVED' | 'REJECTED';
}

export interface UserListResponse {
  content: User[];
  totalPages: number;
  totalElements: number;
  currentPage: number;
  size: number;
}

// 실제 API 응답 구조
export interface UserDetailApiResponse {
  person_info: {
    person_id: number;
    deactivate_reason: string | null; // null이면 활성, not null이면 정지
    email: string;
    name: string;
    identity: 'STUDENT' | 'DOCTOR' | 'ADMIN' | 'OTHER';
    department: string;
    license: string | null; // 인증서류 정보
    id_card_file_url: string | null; // 인증서류 파일 URL
    birth_date: string;
    phone_number: string;
    created_at: string;
    last_login_at: string | null;
    post_count: number;
    reply_count: number;
    approval_status: 'PENDING' | 'APPROVED' | 'REJECTED';
  };
  lectures: {
    lecture_info: {
      id: number;
      title: string;
      thumbnail_url: string;
      tags: string[];
    };
    lecturer_info: {
      id: number;
      name: string;
      introduction: string;
      department?: string;
      image_url: string;
      tags: string[];
    };
    status: 'ENROLLED' | 'IN_PROGRESS' | 'ENDED' | 'INTERVIEWED' | 'ISSUED'; // 강의 상태
    completion_rate: number;
    ended_at: string | null;
    completed_at: string | null;
  }[];
  inquiries: {
    post_id: number;
    title: string;
    content: string;
    post_category: string;
    reply_cnt: number; // 답변 수 (0이면 답변 대기, != 0이면 답변 완료)
    view_cnt: number;
    writer_name: string;
    writer_profile_image_url: string;
    created_at: string;
    updated: boolean;
    pinned: boolean;
  }[];
}

// 컴포넌트에서 사용할 UserDetails (기존 유지하되 확장)
export interface UserDetails extends User {
  deactivateReason: string | null;
  license: string | null;
  idCardFileUrl: string | null;
  approvalStatus: 'PENDING' | 'APPROVED' | 'REJECTED';
  lastLoginAt?: string;
  isActive: boolean; // 필수로 변경
  enrolledLectures: EnrolledLecture[];
  completedLectures: CompletedLecture[];
  inquiries: {
    id: number;
    title: string;
    content: string;
    category: string;
    replyCount: number;
    viewCount: number;
    writerName: string;
    writerProfileImageUrl: string;
    createdAt: string;
    updated: boolean;
    pinned: boolean;
  }[];
}

export interface EnrolledLecture {
  id: number;
  title: string;
  thumbnailUrl: string;
  lecturerName: string;
  lecturerIntroduction: string;
  status: 'ENROLLED' | 'IN_PROGRESS' | 'ENDED' | 'INTERVIEWED' | 'ISSUED';
  progress: number;
  endedAt: string | null;
  completedAt: string | null;
  enrolledAt?: string; // API에서 제공되지 않으므로 optional
}

interface CompletedLecture {
  id: number;
  title: string;
  completedAt: string;
  rating?: number;
}

// API 데이터를 컴포넌트 형식으로 변환하는 함수 - API 원본 구조 유지
const convertToUser = (apiData: UserApiData): User => {
  return {
    id: apiData.person_id,
    name: apiData.name,
    email: apiData.email,
    department: apiData.department,
    affiliation: apiData.department, // department를 affiliation으로도 사용
    phone: apiData.phone_number,
    status: apiData.approval_status, // API 원본값 그대로 사용: PENDING, APPROVED, REJECTED 등
    category: apiData.identity, // API 원본값 그대로 사용: STUDENT, DOCTOR, ADMIN 등
    createdAt: new Date().toISOString(), // API에 없으므로 현재 시간으로 설정
    birthDate: apiData.birth_date,
    postsCount: 0, // API에 없으므로 기본값
    commentsCount: 0, // API에 없으므로 기본값
    // API 원본 필드들 추가
    person_id: apiData.person_id,
    birth_date: apiData.birth_date,
    identity: apiData.identity,
    phone_number: apiData.phone_number,
    approval_status: apiData.approval_status,
    deactivate_reason: apiData.deactivate_reason
  };
};

// UserDetail API 응답을 UserDetails로 변환하는 함수
const convertToUserDetails = (apiResponse: UserDetailApiResponse): UserDetails => {
  const personInfo = apiResponse.person_info; // person_info -> personInfo

  const result = {
    id: personInfo.person_id,
    name: personInfo.name,
    email: personInfo.email,
    department: personInfo.department,
    affiliation: personInfo.department,
    phone: personInfo.phone_number,
    status: 'ACTIVE' as const, // detail에서는 승인된 사용자만 조회
    category: personInfo.identity === 'STUDENT' ? '의대생' : 
              personInfo.identity === 'DOCTOR' ? '의사' : 
              personInfo.identity === 'ADMIN' ? '관리자' : '기타',
    createdAt: personInfo.created_at,
    birthDate: personInfo.birth_date,
    postsCount: personInfo.post_count,
    commentsCount: personInfo.reply_count,
    
    // 추가 필드들
    deactivateReason: personInfo.deactivate_reason,
    license: personInfo.license,
    idCardFileUrl: personInfo.id_card_file_url,
    approvalStatus: personInfo.approval_status,
    lastLoginAt: personInfo.last_login_at || undefined,
    isActive: personInfo.deactivate_reason === null, // deactivate_reason이 null이면 활성, not null이면 정지
    enrolledLectures: apiResponse.lectures.map((lecture) => ({
      id: lecture.lecture_info.id,
      title: lecture.lecture_info.title,
      thumbnailUrl: lecture.lecture_info.thumbnail_url,
      lecturerName: lecture.lecturer_info.name,
      lecturerIntroduction: lecture.lecturer_info.department || lecture.lecturer_info.introduction || '',
      status: lecture.status,
      progress: lecture.completion_rate,
      endedAt: lecture.ended_at,
      completedAt: lecture.completed_at
    })),
    completedLectures: [], // 완료 강의는 별도 분류가 필요할 수 있음
    inquiries: apiResponse.inquiries.map((inquiry) => ({
      id: inquiry.post_id,
      title: inquiry.title,
      content: inquiry.content,
      category: inquiry.post_category,
      replyCount: inquiry.reply_cnt,
      viewCount: inquiry.view_cnt,
      writerName: inquiry.writer_name,
      writerProfileImageUrl: inquiry.writer_profile_image_url,
      createdAt: inquiry.created_at,
      updated: inquiry.updated,
      pinned: inquiry.pinned
    }))
  }
  return result;
};

export interface LectureWatchProgress {
  personId: number;
  personName: string;
  lectureId: number;
  lectureTitle: string;
  lecturerName: string;
  durationSeconds: number;
  status: EnrolledLecture['status'];
  completionRate: number;
  endedAt: string | null;
  completedAt: string | null;
  watchedRanges: Array<{ startSecond: number; endSecond: number }>;
}

interface LectureWatchProgressApiResponse {
  person_id: number;
  person_name: string;
  lecture_id: number;
  lecture_title: string;
  lecturer_name: string;
  duration_seconds: number;
  status: EnrolledLecture['status'];
  completion_rate: number;
  ended_at: string | null;
  completed_at: string | null;
  watched_ranges: Array<{ start_second: number; end_second: number }>;
}

const mapLectureWatchProgress = (data: LectureWatchProgressApiResponse): LectureWatchProgress => ({
  personId: data.person_id,
  personName: data.person_name,
  lectureId: data.lecture_id,
  lectureTitle: data.lecture_title,
  lecturerName: data.lecturer_name,
  durationSeconds: data.duration_seconds,
  status: data.status,
  completionRate: data.completion_rate,
  endedAt: data.ended_at,
  completedAt: data.completed_at,
  watchedRanges: data.watched_ranges.map((range) => ({
    startSecond: range.start_second,
    endSecond: range.end_second,
  })),
});

// 회원 API 서비스
export const userApi = {
  // 회원 목록 조회 (필터 포함)
  getUsers: async (params: UserListParams = {}): Promise<UserListResponse> => {
    // 새로운 필터 API 사용 시 필요한 매개변수 구성
    const queryParams: any = {};
    
    // 기본 페이지네이션
    if (params.page !== undefined) queryParams.page = params.page;
    if (params.size !== undefined) queryParams.size = params.size;
    
    // 새로운 필터 매개변수들
    if (params.identity) queryParams.identity = params.identity;
    if (params.department) queryParams.department = params.department;
    if (params.approvalStatus) queryParams.approvalStatus = params.approvalStatus;
    
    // 검색어 (기존 방식 유지)
    if (params.search) queryParams.search = params.search;
    
    const response = await apiClient.get('/api/admin/users/info/list', { 
      params: queryParams 
    });
    /// console.log('GET /api/admin/users/info/list 응답',{response});
    const result: ApiResponse<UserListApiResponse> = response.data;
    
    return {
      content: result.data.content.map(convertToUser),
      totalPages: result.data.totalPages,
      totalElements: result.data.totalElements,
      currentPage: result.data.number,
      size: result.data.size,
    };
  },

  // 승인 대기 회원 목록 조회
  getApprovalPendingUsers: async (params: UserListParams = {}): Promise<UserListResponse> => {
    const response = await apiClient.get('/api/admin/users/approve/list', { params });
    const result: ApiResponse<UserListApiResponse> = response.data;
    
    return {
      content: result.data.content.map(convertToUser),
      totalPages: result.data.totalPages,
      totalElements: result.data.totalElements,
      currentPage: result.data.number,
      size: result.data.size,
    };
  },

  // 회원 상세 조회
  getUserDetails: async (personId: number): Promise<UserDetails> => {
    const response = await apiClient.get(`/api/admin/users/${personId}/details`);
    const result: ApiResponse<UserDetailApiResponse> = response.data;
    return convertToUserDetails(result.data);
  },

  getLectureWatchProgress: async (personId: number, lectureId: number): Promise<LectureWatchProgress> => {
    const response = await apiClient.get(`/api/admin/users/${personId}/lectures/${lectureId}/progress`);
    const result: ApiResponse<LectureWatchProgressApiResponse> = response.data;
    return mapLectureWatchProgress(result.data);
  },

  // 회원의 특정 강의 수강완료 강제 처리 (진도율 무관, 멱등)
  completeLecture: async (personId: number, lectureId: number): Promise<LectureWatchProgress> => {
    const response = await apiClient.patch(`/api/admin/users/${personId}/lectures/${lectureId}/complete`);
    const result: ApiResponse<LectureWatchProgressApiResponse> = response.data;
    return mapLectureWatchProgress(result.data);
  },

  // 회원 계정 정지/해제
  toggleUserFreeze: async (personId: number, freeze: boolean, reason?: string): Promise<any> => {
    const params = new URLSearchParams();
    params.append('freeze', freeze.toString());
    if (reason && freeze) {
      params.append('reason', reason);
    }
    
    const response = await apiClient.patch(`/api/admin/users/${personId}/freeze?${params.toString()}`);
    return response.data;
  },

  // 회원 승인
  approveUser: async (personId: number): Promise<void> => {
    await apiClient.patch(`/api/admin/users/approve/${personId}?approved=true`);
  },

  // 회원 반려
  rejectUser: async (personId: number, reason: string): Promise<void> => {
    const encodedReason = encodeURIComponent(reason);
    await apiClient.patch(`/api/admin/users/approve/${personId}?approved=false&reason=${encodedReason}`);
  },

  // 승인 대기 사용자 목록 조회 (회원 자격 관리용)
  getPendingUsers: async (params: UserListParams = {}): Promise<UserListResponse> => {
    // 승인 상태가 PENDING인 사용자만 조회
    const queryParams = {
      ...params,
      approvalStatus: 'PENDING'
    };
    
    const response = await apiClient.get('/api/admin/users/info/list', { params: queryParams });
    const result: ApiResponse<UserListApiResponse> = response.data;
    
    return {
      content: result.data.content.map(convertToUser),
      totalPages: result.data.totalPages,
      totalElements: result.data.totalElements,
      currentPage: result.data.number,
      size: result.data.size,
    };
  },

  // 비밀번호 재설정
  changePassword: async (email: string, newPassword: string): Promise<any> => {
    // 비밀번호 암호화
    const encryptedPassword = await encryptPassword(newPassword);
    
    const response = await apiClient.patch('/api/auth/change-password', {
      email,
      new_password: encryptedPassword
    });
    return response.data;
  }
};
